// Roles come from the `loggedInAs` claim of a minted token (decoded server-side
// and stored on each token). This module is the single source of truth for what
// the roles mean and which kind of user each feature expects — so dropdowns can
// show a readable role, preselect a sensible default, and warn on a mismatch.

// Staff (HQ / field) roles — these can manage shop stock and initiate
// allocations. Anything else is treated as an agent / view-only here.
export const STAFF_ROLES = new Set(['SC', 'HQSM', 'FSA', 'FSM', 'SE', 'SFSM', 'DRM', 'RM', 'HOS', 'FSLM', 'SRM'])

// The sales-agent role — agents register customers and accept/reject allocations.
export const AGENT_ROLE = 'SalesAgent'

// Best-effort friendly names. Staff roles are usually referred to by their
// acronym, so we only expand the ones we're confident about and otherwise show
// the raw code.
const ROLE_LABELS: Record<string, string> = {
  SalesAgent: 'Sales Agent',
  SC: 'Stock Controller',
}

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'unknown role'
  return ROLE_LABELS[role] ?? role
}

export function isStaffRole(role: string | null | undefined): boolean {
  return !!role && STAFF_ROLES.has(role)
}

export function isAgentRole(role: string | null | undefined): boolean {
  return role === AGENT_ROLE
}

// What kind of user a feature is meant for. Mismatches are surfaced as a soft
// warning — never a hard block (this is a dev toolbox; you may want to try).
export type ExpectedRole = 'staff' | 'agent'

export interface RoleExpectation {
  kind: ExpectedRole
  // Short noun phrase for messages, e.g. "a sales agent".
  who: string
  // Why, for the warning tooltip/banner.
  reason: string
}

export const EXPECT_AGENT: RoleExpectation = {
  kind: 'agent',
  who: 'a sales agent',
  reason: 'Customer registration is done by a Sales Agent.',
}

export const EXPECT_STAFF: RoleExpectation = {
  kind: 'staff',
  who: 'a staff member (SC / HQSM / FSA …)',
  reason: 'Initiating an allocation needs a staff role.',
}

// True if the role satisfies the expectation. Null/unknown roles never satisfy
// (so we warn rather than silently assume).
export function roleSatisfies(exp: RoleExpectation, role: string | null | undefined): boolean {
  return exp.kind === 'staff' ? isStaffRole(role) : isAgentRole(role)
}
